import { Box, Button, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useMobile } from '../../hooks';
import SimpleDialog from '../dialog/SimpleDialog';
import FormStatusWrapper from '../form-messages/form-status-wrapper';

interface DeleteModalProps {
  title: string;
  visible: boolean;
  content: JSX.Element;
  onClose: () => void;
  onCancel: () => void;
  onDelete: () => Promise<void>;
  loading: boolean;
  success: boolean;
  error: boolean;
}

interface RenderDeleteModalFormProps {
  loading: boolean;
  success: boolean;
  error: boolean;
  modalContent: JSX.Element;
  setDisabled: (disabled: boolean) => void;
  isMobile: boolean;
}

export function getMarginTop(
  success: boolean,
  error: boolean,
  loading: boolean,
  isMobile: boolean,
  mobileMargin: string,
  desktopMargin: string
): string {
  if (success || error || loading) {
    return '';
  }
  return isMobile ? mobileMargin : desktopMargin;
}

function RenderDeleteModalForm(props: RenderDeleteModalFormProps) {
  const { modalContent, setDisabled, loading, error, success, isMobile } =
    props;

  return (
    <Box className="delete-modal-content">
      <FormStatusWrapper
        loading={loading}
        loadingMessage="Deleting..."
        error={error}
        errorMessage="Error deleting items"
        success={success}
      >
        <Box>
          <Box>{modalContent}</Box>
          <Box className="delete-confirm">
            <Typography sx={{ mb: 1 }}>
              If you are sure, type &apos;delete&apos; below and click the
              delete button.
            </Typography>
            <TextField
              aria-label="delete-confirmation-input"
              type="text"
              id="confirm-delete-input"
              data-testid="confirm-delete-input"
              inputProps={{ 'data-testid': 'content-confirm-delete-input' }}
              onChange={(e) =>
                e.target.value === 'delete'
                  ? setDisabled(false)
                  : setDisabled(true)
              }
              size="small"
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              sx={{
                marginTop: getMarginTop(
                  success,
                  error,
                  loading,
                  isMobile,
                  '2em',
                  '0'
                ),
              }}
            />
          </Box>
        </Box>
      </FormStatusWrapper>
    </Box>
  );
}

function DeleteModal(props: DeleteModalProps) {
  const {
    title,
    visible,
    onClose,
    onCancel,
    content,
    onDelete,
    loading,
    success,
    error,
  } = props;

  const [disabled, setDisabled] = useState(true);
  const isMobile = useMobile();

  return (
    <SimpleDialog
      onClose={onClose}
      title={title}
      width={500}
      open={visible}
      className="delete-modal"
      actions={
        <Box
          sx={{
            display: 'flex',
            marginTop: getMarginTop(
              success,
              error,
              loading,
              isMobile,
              '-5.9em',
              '-3.9em'
            ),
            px: '1rem',
            justifyContent: isMobile ? 'space-evenly' : 'space-between',
            width: '50%',
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              m: '1rem 0rem',
              width: '74px',
            }}
            data-testid="add-vessel-close-button"
          >
            {success || error ? 'Close' : 'Cancel'}
          </Button>
          {!loading && !error && !success && (
            <Button
              variant="contained"
              data-testid="delete-modal-button"
              disabled={disabled}
              onClick={onDelete}
              sx={{
                m: '1rem 0',
                width: '74px',
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      }
    >
      <RenderDeleteModalForm
        loading={loading}
        error={error}
        success={success}
        modalContent={content}
        setDisabled={setDisabled}
        isMobile={isMobile}
      />
    </SimpleDialog>
  );
}

export default DeleteModal;
